import styled, { css } from 'styled-components';

import { breakpoints } from 'theme';

const primary = '#00696b';
const primaryLight = '#00a4a6';
const onSurface = '#191c1e';
const onVariant = '#3d4949';
const muted = '#64748b';
const border = '#e2e8f0';
const cardShadow = '0 8px 32px rgba(0, 50, 51, 0.12)';

const planGradient = css`
  background: linear-gradient(135deg, ${primary}, ${primaryLight});
`;

export const ModalPanel = styled.div`
  position: relative;
  width: min(480px, calc(100vw - 32px));
  max-height: calc(100svh - 48px);
  overflow: auto;
  background: #fff;
  border-radius: 16px;
  box-shadow: ${cardShadow};
  padding: 24px;

  @media ${breakpoints.downSmPlus} {
    padding: 20px 16px;
    border-radius: 12px;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: ${muted};
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    background: #f8fafc;
    color: ${onSurface};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const ModalTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.25;
  color: ${onSurface};

  @media ${breakpoints.downSmPlus} {
    font-size: 18px;
  }
`;

export const ModalBody = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  font-weight: 500;
  color: ${onVariant};
`;

export const ModalBodyBold = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.55;
  font-weight: 700;
  color: ${onSurface};
`;

export const ModeSwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0;
`;

export const ModeCard = styled.div<{ $highlight?: boolean }>`
  flex: 1;
  min-width: 0;
  padding: 16px 12px;
  border-radius: 12px;
  border: 2px solid ${({ $highlight }) => ($highlight ? primaryLight : border)};
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(0, 164, 166, 0.06)' : '#f8fafc'};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
`;

export const ModeIconBadge = styled.div<{ $emphasis?: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid
    ${({ $emphasis }) => ($emphasis ? 'rgba(0, 164, 166, 0.28)' : border)};
  box-shadow: ${({ $emphasis }) =>
    $emphasis
      ? '0 4px 14px rgba(0, 164, 166, 0.14)'
      : '0 2px 8px rgba(15, 23, 42, 0.06)'};
  color: ${({ $emphasis }) => ($emphasis ? primary : muted)};

  > svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }
`;

export const ModeIconGraphic = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  svg {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const ModeCardLabel = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${muted};
`;

export const ModeCardValue = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${onSurface};
  line-height: 1.3;
`;

export const ModeArrow = styled.div`
  flex-shrink: 0;
  color: ${primaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CycleNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 20px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid ${border};
  font-size: 12px;
  line-height: 1.5;
  font-weight: 500;
  color: ${muted};
`;

export const AddCardHero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 8px 0 4px;
`;

export const AddCardIconRing = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  ${planGradient}
  color: #fff;
  box-shadow: 0 8px 24px rgba(0, 105, 107, 0.28);
`;

export const AddCardCallout = styled.div`
  margin: 16px 0 20px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(0, 164, 166, 0.35);
  background: linear-gradient(
    135deg,
    rgba(0, 164, 166, 0.08),
    rgba(0, 105, 107, 0.04)
  );
  text-align: left;
`;

export const AddCardCalloutTitle = styled.p`
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 800;
  color: ${primary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const AddCardCalloutBody = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  font-weight: 500;
  color: ${onVariant};
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;

  @media ${breakpoints.downSmPlus} {
    flex-direction: column-reverse;
  }
`;

export const ModalBtnSecondary = styled.button`
  flex: 1;
  padding: 12px 20px;
  border-radius: 12px;
  border: 2px solid ${border};
  background: #fff;
  color: ${onVariant};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const ModalBtnPrimary = styled.button`
  flex: 1.2;
  padding: 12px 20px;
  border-radius: 12px;
  border: none;
  ${planGradient}
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;

  &:hover:not(:disabled) {
    opacity: 0.92;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

export const ModalError = styled.p`
  margin: 16px 0 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 90, 90, 0.08);
  border: 1px solid rgba(255, 90, 90, 0.25);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
  color: ${({ theme }) => theme.color.bittersweet};
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.88);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
`;

export const LoaderSlot = styled.div`
  position: relative;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LoadingText = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${onVariant};
`;

export const ContentArea = styled.div`
  position: relative;
`;
